import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@clerk/clerk-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { api_endpoint } from "@/constants/constant";
import { ClipLoader } from "react-spinners";

function normalizeSelectedCohorts(selectedCohorts) {
  const rows = Array.isArray(selectedCohorts) ? selectedCohorts : [];

  return rows
    .map((row) => {
      const raw = row?.raw ?? row;
      const id = raw?.id ?? raw?.cohort_id ?? row?.id;
      const name = raw?.name ?? row?.name;
      return {
        id: id != null ? Number(id) : null,
        name: name != null ? String(name) : "",
      };
    })
    .filter((c) => Number.isFinite(c.id));
}

export default function BatchDeleteCohorts({
  open,
  onOpenChange,
  selectedCohorts,
  onDeleted,
  refetchCohorts,
}) {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [errorDetails, setErrorDetails] = useState(null);

  useEffect(() => {
    if (!open) return;
    setConfirmText("");
    setErrorDetails(null);
  }, [open]);

  const cohorts = useMemo(
    () => normalizeSelectedCohorts(selectedCohorts),
    [selectedCohorts]
  );

  const cohortIds = useMemo(() => cohorts.map((c) => c.id), [cohorts]);
  const cohortCount = cohortIds.length;

  const canSubmit = cohortCount > 0 && confirmText.trim().toUpperCase() === "DELETE";

  const handleCopyErrorDetails = async () => {
    try {
      const text = JSON.stringify(
        {
          request: {
            url: `${api_endpoint}/cohort/batch-delete`,
            method: "DELETE",
            payload: errorDetails?.payload,
          },
          response: {
            status: errorDetails?.status,
            message: errorDetails?.message,
            data: errorDetails?.responseData,
          },
        },
        null,
        2
      );
      await navigator.clipboard.writeText(text);
      toast.success("Copied debug details.");
    } catch (_err) {
      toast.error("Could not copy debug details.");
    }
  };

  const handleDelete = async () => {
    if (!canSubmit) return;

    setLoading(true);
    setErrorDetails(null);

    const payload = {
      cohort_ids: cohortIds,
    };

    try {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      };

      const res = await axios.delete(`${api_endpoint}/cohort/batch-delete`, reqConfig);
      const data = res?.data;

      const totalDeleted =
        data?.total_deleted != null ? Number(data.total_deleted) : null;
      const totalErrors =
        data?.total_errors != null ? Number(data.total_errors) : null;

      const deletedCount = Number.isFinite(totalDeleted)
        ? totalDeleted
        : Array.isArray(data?.deleted_cohort_ids)
          ? data.deleted_cohort_ids.length
          : cohortCount;

      if (Number.isFinite(totalErrors) && totalErrors > 0) {
        toast.success(
          `Deleted ${deletedCount} cohort${deletedCount === 1 ? "" : "s"}. ${totalErrors} failed.`
        );
      } else {
        toast.success(
          `Deleted ${deletedCount} cohort${deletedCount === 1 ? "" : "s"} successfully.`
        );
      }

      onOpenChange(false);
      onDeleted?.(data);
      // Defer so parent refetch isn't affected by dialog state batching.
      if (refetchCohorts) setTimeout(() => refetchCohorts(), 0);
    } catch (error) {
      const status = error?.response?.status;
      const responseData = error?.response?.data;
      const responseHeaders = error?.response?.headers;
      const message =
        responseData?.message ||
        responseData?.error ||
        error?.message ||
        "Request failed";

      console.error("Error batch-deleting cohorts:", {
        status,
        message,
        responseData,
        responseHeaders,
        payload,
      });

      setErrorDetails({
        status,
        message,
        responseData,
        payload,
      });

      toast.error(status ? `Failed to delete cohorts (${status}).` : "Failed to delete cohorts.");
    } finally {
      setLoading(false);
    }
  };

  const previewNames = cohorts
    .map((c) => c.name)
    .filter(Boolean)
    .slice(0, 4);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md h-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-(--neutral-900)">
            Delete Cohorts
          </DialogTitle>
        </DialogHeader>

        <div className="font-body text-(--neutral-800)">
          Are you sure you want to delete <b>{cohortCount}</b> cohort{cohortCount === 1 ? "" : "s"}?
          This action cannot be undone.
          <div className="mt-3 text-(--color-primary) font-bold">
            Warning: Deleting cohorts will also remove all students assigned to them (CASCADE).
          </div>

          {previewNames.length ? (
            <div className="mt-3 rounded-md border border-(--neutral-300) bg-(--surface-base) p-3">
              <div className="font-heading text-(--neutral-900)">Selected</div>
              <div className="mt-1 text-sm text-(--neutral-700)">
                {previewNames.join(", ")}
                {cohortCount > previewNames.length ? ` +${cohortCount - previewNames.length} more` : ""}
              </div>
            </div>
          ) : null}
        </div>

        <div className="mt-4">
          <label className="font-body text-sm text-(--neutral-800)">
            Type <b>DELETE</b> to confirm:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="border rounded px-2 py-1 w-full mt-1 text-(--neutral-900)"
            placeholder='Type "DELETE"'
          />
        </div>

        {errorDetails ? (
          <div className="mt-4 rounded-md border border-(--color-destructive) bg-(--surface-base) p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-heading text-(--color-destructive)">
                  Delete failed{errorDetails?.status ? ` (${errorDetails.status})` : ""}
                </div>
                <div className="mt-1 font-body text-sm text-(--neutral-700)">
                  {String(errorDetails?.message || "The server rejected the request.")}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="border-(--neutral-300)"
                onClick={handleCopyErrorDetails}
              >
                Copy debug
              </Button>
            </div>

            <div className="mt-3 grid gap-3">
              <div>
                <div className="font-body text-xs font-semibold text-(--neutral-700)">
                  Request payload
                </div>
                <pre className="mt-1 max-h-40 overflow-auto rounded bg-(--neutral-100) p-2 text-xs text-(--neutral-900)">
                  {JSON.stringify(errorDetails?.payload, null, 2)}
                </pre>
              </div>
              <div>
                <div className="font-body text-xs font-semibold text-(--neutral-700)">
                  Response body
                </div>
                <pre className="mt-1 max-h-40 overflow-auto rounded bg-(--neutral-100) p-2 text-xs text-(--neutral-900)">
                  {JSON.stringify(errorDetails?.responseData, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant=""
            onClick={handleDelete}
            disabled={loading || !canSubmit}
          >
            {loading ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="#fff" />
              </span>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
